import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { FaBars, FaTimes, FaUser } from 'react-icons/fa';
import logo from '../assets/logos/oltre-frontiera-logo.png';
import './Header.css';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const { user, logout, isAdmin } = useAuth();
  const { t, locale, language, setLanguage } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const id = window.setInterval(tick, 60 * 1000);
    return () => window.clearInterval(id);
  }, []);

  const { dateLabel, timeLabel, isoDateTime } = useMemo(() => {
    const d = now;
    const dateLabel = d.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const timeLabel = d.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return {
      dateLabel,
      timeLabel,
      isoDateTime: d.toISOString(),
    };
  }, [now, locale]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo">
          <img src={logo} alt="Oltre Frontiera" className="logo-image" />
        </Link>

        <button 
          className="menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <nav
          className={`header-nav ${menuOpen ? 'nav-open' : ''}`}
          aria-label="Navigation principale"
        >
          <div className="header-top-row">
            <div className="nav-primary">
              <Link to="/" onClick={() => setMenuOpen(false)}>{t('nav.home')}</Link>
              <Link to="/billets" onClick={() => setMenuOpen(false)}>{t('nav.tickets')}</Link>
              <Link to="/partenaires" onClick={() => setMenuOpen(false)}>{t('nav.partners')}</Link>
              <Link to="/faq" onClick={() => setMenuOpen(false)}>{t('nav.faq')}</Link>
              <Link to="/contact" onClick={() => setMenuOpen(false)}>{t('nav.contact')}</Link>
              <Link to="/paiement-et-comptes" onClick={() => setMenuOpen(false)}>{t('nav.payment')}</Link>
              {user && (
                <Link to="/juventus-card" onClick={() => setMenuOpen(false)}>{t('nav.juventusCard')}</Link>
              )}
              <div className="header-lang">
                <select
                  id="site-language"
                  className="header-lang-select"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  aria-label={t('home.languageLabel')}
                >
                  <option value="fr">🇫🇷</option>
                  <option value="it">🇮🇹</option>
                </select>
              </div>
            </div>

            <div className="nav-auth-row">
              {user ? (
                <>
                  {isAdmin() && (
                    <Link to="/admin" className="admin-link" onClick={() => setMenuOpen(false)}>
                      {t('nav.admin')}
                    </Link>
                  )}
                  <Link to="/account" className="user-menu" onClick={() => setMenuOpen(false)}>
                    <FaUser />
                    <span>{user.firstName}</span>
                  </Link>
                  <button type="button" className="btn-logout" onClick={handleLogout}>
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-login" onClick={() => setMenuOpen(false)}>
                    {t('nav.login')}
                  </Link>
                  <Link to="/register" className="btn-register" onClick={() => setMenuOpen(false)}>
                    {t('nav.register')}
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="header-nav-meta">
            <div className="header-home-meta">
              <time dateTime={isoDateTime} className="header-datetime" aria-live="polite">
                <span className="header-date">{dateLabel}</span>
                <span className="header-datetime-sep" aria-hidden="true">
                  {' · '}
                </span>
                <span className="header-time">{timeLabel}</span>
              </time>
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;


